/**
 * <s-product-gallery> — one-slide-at-a-time product gallery with thumbnails and arrows.
 *
 * Required markup inside the element:
 *   [data-product-gallery]        — horizontal scroll-snap track
 *   [data-gallery-slide]          — each full-width slide in the track
 *   button[data-gallery-thumb="n"] — thumbnail that shows slide n (0-based)
 *   button[data-gallery-prev] / button[data-gallery-next] — optional arrows
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        this.show(Number(thumb.dataset.galleryThumb));
        return;
      }
      if (event.target.closest('[data-gallery-prev]')) this.show(this.index - 1);
      if (event.target.closest('[data-gallery-next]')) this.show(this.index + 1);
    }, opts);

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });


    // Mouse drag (e.g. the mobile preview on a computer): drag the photo left/right to change slides.
    // Touch keeps the browser's native swipe.
    let drag = null;
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.track.scrollWidth <= this.track.clientWidth) return;
      drag = { x: event.clientX, left: this.track.scrollLeft, start: this.index, moved: false };
    }, opts);
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 6) return;
      if (!drag.moved) {
        drag.moved = true;
        this.track.setAttribute('data-dragging', '');
      }
      this.track.scrollLeft = drag.left - dx;
    }, opts);
    window.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const { moved, start, x } = drag;
      drag = null;
      if (!moved) return;
      this.track.removeAttribute('data-dragging');
      const dx = event.clientX - x;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const step = Math.abs(dx) > 40 ? (dx < 0 ? 1 : -1) * (rtl ? -1 : 1) : 0;
      this.show(Math.max(0, Math.min(this.slides.length - 1, start + step)));
      // Swallow the click that follows a drag so it doesn't open the zoom view.
      const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
      this.track.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 0);
    }, opts);

    // Picking a color or size with its own image shows that image (1-based media position).
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement) || !input.closest('s-variant-picker')) return;
      const position = Number(input.dataset.mediaPosition || input.selectedOptions?.[0]?.dataset?.mediaPosition || 0);
      if (position > 0) this.show(position - 1);
    }, opts);

    const start = Number(this.dataset.startPosition || 0) - 1;
    if (start > 0) {
      requestAnimationFrame(() => {
        const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        this.track.scrollTo({ left: start * this.track.clientWidth * direction, behavior: 'instant' });
        this.#sync(start);
      });
      return;
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get slides() {
    return this.track.querySelectorAll('[data-gallery-slide]');
  }

  get index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  show(index) {
    const count = this.slides.length;
    if (!count) return;
    const target = (index + count) % count;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: target * this.track.clientWidth * direction });
    this.#sync(target);
  }

  #sync(forced) {
    const active = forced ?? this.index;
    const counter = this.querySelector("[data-gallery-current]");
    if (counter) counter.textContent = String(active + 1);
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const isActive = Number(thumb.dataset.galleryThumb) === active;
      thumb.toggleAttribute('data-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      const strip = thumb.parentElement;
      if (!isActive || !strip) return;
      // Keep the active thumbnail visible inside its strip without moving the page.
      if (strip.scrollWidth > strip.clientWidth) {
        const left = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - 8 });
        }
      }
      if (strip.scrollHeight > strip.clientHeight) {
        const top = thumb.getBoundingClientRect().top - strip.getBoundingClientRect().top + strip.scrollTop;
        if (top < strip.scrollTop || top + thumb.offsetHeight > strip.scrollTop + strip.clientHeight) {
          strip.scrollTo({ top: top - 8 });
        }
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
